import './GoalBar.css';
import { DualBar } from '../../components/DualBar';
import { t } from '../../copy/strings';
import { distanceTo50, fmtPct, fmtPoints, NBSP, pctDecimals } from '../../format/numbers';
import type { Candidate } from '../../model/types';
import { partyOf } from '../../theme/parties';

/** Um lado da barra: o `HeroSide` de `features/hero/model.ts` (§7.2) satisfaz esta forma. */
export interface GoalSide {
  candidate: Pick<Candidate, 'number' | 'party_code' | 'display_name'>;
  /** Fração dos válidos (0–1). */
  share: number;
  votes: number;
}

export interface GoalBarProps {
  /** [número menor, número maior] (`HeroModel.sides`): A cresce da esquerda, B da direita (N01, N06). */
  sides: readonly [GoalSide, GoalSide];
  /** `mathDecision` ou `tseResult` presente (`HeroModel.decided !== null`): sem rótulo "50%" nem rodapé; marca 4/4. */
  decided?: boolean;
  /** `HeroModel.goalDecimals` (2 com distância < 1 ponto, senão 1); sem ele, `pctDecimals` das fatias. */
  decimals?: 1 | 2;
}

/** Rodapé de um lado (§7.6 bloco 3) pelas chaves `goal.*`, com a classificação de `distanceTo50` (§13.4). */
function footOf(share: number, decimals: 1 | 2): string {
  const d = distanceTo50(share, decimals);
  if (d === null) return t('common.dash');
  switch (d.kind) {
    case 'passed':
      return t('goal.passed');
    case 'tie':
      return t('goal.tie');
    case 'near':
      return t(decimals === 1 ? 'goal.near1' : 'goal.near2');
    case 'missing': {
      const pontos = fmtPoints(d.distance, decimals);
      return t(pontos.endsWith(`${NBSP}ponto`) ? 'goal.missingOne' : 'goal.missing', { pontos });
    }
  }
}

/**
 * Textos do rodapé [A, B] (§7.6 bloco 3): "em 50%" nos dois com votos iguais; senão, por lado, "passou de 50%",
 * "a menos de 0,1 ponto" / "a menos de 0,01 ponto", "falta 1,0 ponto" ou "faltam {d} pontos". O mesmo modelo para os
 * dois lados (N03): trocar 13 e 22 nos dados só troca a ordem da saída.
 */
export function goalFoot(sides: readonly [GoalSide, GoalSide], decimals: 1 | 2): [string, string] {
  if (sides[0].votes === sides[1].votes) return [t('goal.tie'), t('goal.tie')];
  return [footOf(sides[0].share, decimals), footOf(sides[1].share, decimals)];
}

/** `goal.aria` (§7.6 bloco 7, §15.4.2): percentuais com 1 casa. */
export function goalAria(sides: readonly [GoalSide, GoalSide]): string {
  return t('goal.aria', {
    A: sides[0].candidate.display_name,
    pA: fmtPct(sides[0].share, 1),
    B: sides[1].candidate.display_name,
    pB: fmtPct(sides[1].share, 1),
  });
}

/**
 * Barra bicolor com a meta de 50% (§7.6) sobre o `DualBar` (§3.8). O contêiner `goal-bar` é o `role="img"` com
 * `goal.aria`; o rodapé ("faltam…" / "passou de 50%") é texto visível comum, FORA do `img` (§15.4.2), por isso vem como
 * irmão (`goal-bar-foot`) e não como filho. Decidido: sem rótulo "50%" e sem rodapé no DOM (AC-7.6-05).
 */
export function GoalBar({ sides, decided = false, decimals }: GoalBarProps) {
  const [a, b] = sides;
  const aria = goalAria(sides);
  const dec = decimals ?? pctDecimals(a.share, b.share);
  const foot = decided ? null : goalFoot(sides, dec);
  return (
    <>
      <div
        class="goal-bar"
        data-testid="goal-bar"
        data-state={decided ? 'decided' : 'counting'}
        role="img"
        aria-label={aria}
      >
        {!decided && (
          <span class="goal-bar__label" aria-hidden="true">
            {t('goal.fifty')}
          </span>
        )}
        <DualBar
          shares={[a.share, b.share]}
          parties={[partyOf(a.candidate), partyOf(b.candidate)]}
          names={[a.candidate.display_name, b.candidate.display_name]}
          decided={decided}
          variant="hero"
          label={aria}
        />
      </div>
      {foot && (
        <p class="goal-bar__foot" data-testid="goal-bar-foot">
          <span class="goal-bar__side">{foot[0]}</span>
          <span class="goal-bar__side">{foot[1]}</span>
        </p>
      )}
    </>
  );
}
